import React, { useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Collapse,
  Badge,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
} from '@mui/material';
import {
  Analytics,
  Timeline,
  TrendingUp,
  Insights,
  Psychology,
  Memory,
  History,
  Star,
  Archive,
  Delete,
  Edit,
  MoreVert,
  ExpandLess,
  ExpandMore,
  Chat,
  Person,
  Group,
  Settings,
  Help,
  Info,
} from '@mui/icons-material';

function ChatSidePanel({
  open,
  onClose,
  currentConversation,
  conversations = [],
  analytics = {},
  insights = [],
  onSelectConversation,
  onDeleteConversation,
  onArchiveConversation,
  onFavoriteConversation,
}) {
  const [expandedSections, setExpandedSections] = useState({
    analytics: true,
    insights: true,
    recent: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const analyticsData = {
    totalMessages: analytics.totalMessages || 0,
    avgResponseTime: analytics.avgResponseTime || '2.3s',
    userSatisfaction: analytics.userSatisfaction || 4.2,
    topicsDiscussed: analytics.topicsDiscussed || 5,
    ...analytics,
  };

  const insightsData = insights.length > 0 ? insights : [
    { type: 'topic', content: 'AI and Machine Learning', confidence: 0.95 },
    { type: 'sentiment', content: 'Positive interaction', confidence: 0.87 },
    { type: 'intent', content: 'Information seeking', confidence: 0.92 },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: 320,
          bgcolor: 'background.paper',
          borderLeft: 1,
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Chat Insights
        </Typography>
      </Box>

      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
        {/* Analytics Section */}
        <Box sx={{ mb: 3 }}>
          <ListItemButton onClick={() => toggleSection('analytics')}>
            <ListItemIcon>
              <Analytics />
            </ListItemIcon>
            <ListItemText primary="Analytics" />
            {expandedSections.analytics ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={expandedSections.analytics} timeout="auto" unmountOnExit>
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Messages
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {analyticsData.totalMessages}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Avg Response Time
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {analyticsData.avgResponseTime}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    User Satisfaction
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(analyticsData.userSatisfaction / 5) * 100}
                      sx={{ flex: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {analyticsData.userSatisfaction}
                      /5
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Topics Discussed
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {analyticsData.topicsDiscussed}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Collapse>
        </Box>

        {/* Insights Section */}
        <Box sx={{ mb: 3 }}>
          <ListItemButton onClick={() => toggleSection('insights')}>
            <ListItemIcon>
              <Insights />
            </ListItemIcon>
            <ListItemText primary="AI Insights" />
            {expandedSections.insights ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={expandedSections.insights} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 4 }}>
              {insightsData.map((insight, index) => (
                <Card key={index} sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {insight.content}
                      </Typography>
                      <Chip
                        label={`${Math.round(insight.confidence * 100)}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {insight.type}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Collapse>
        </Box>

        {/* Recent Conversations */}
        <Box sx={{ mb: 3 }}>
          <ListItemButton onClick={() => toggleSection('recent')}>
            <ListItemIcon>
              <History />
            </ListItemIcon>
            <ListItemText primary="Recent Conversations" />
            {expandedSections.recent ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={expandedSections.recent} timeout="auto" unmountOnExit>
            <List sx={{ pl: 4 }}>
              {conversations.slice(0, 5).map((conversation) => (
                <ListItem
                  key={conversation.id}
                  sx={{ px: 0, py: 0.5 }}
                  secondaryAction={(
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {conversation.isFavorite && (
                        <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                      )}
                      <IconButton size="small">
                        <MoreVert sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                >
                  <ListItemButton
                    onClick={() => onSelectConversation(conversation.id)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={conversation.name}
                      secondary={`${conversation.messageCount || 0} messages`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontWeight: 500 },
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>

        {/* Quick Actions */}
        <Divider sx={{ my: 2 }} />
        <List>
          <ListItem>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText primary="Chat Settings" />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <Help />
            </ListItemIcon>
            <ListItemText primary="Help & Support" />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <Info />
            </ListItemIcon>
            <ListItemText primary="About" />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
}

export default ChatSidePanel;
